import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Check, Copy } from 'lucide-react';
import { useEffect, useState } from 'react';
import { cn } from "../lib/cn.js";
/**
 * Copiar um código para a área de transferência — F248.
 *
 * *"No editar do produto tem que mostrar o id do produto."* O código aparece
 * em várias fichas, e cada uma tinha a sua cópia escrita à mão — nove, cada uma
 * um pouco diferente. Duas falhavam caladas no que a loja de verdade usa: o
 * Aeris servido em `http://` na rede da loja, onde o navegador não oferece
 * `navigator.clipboard`.
 *
 * Por isso há o recurso do `execCommand('copy')`, por uma caixa de texto
 * escondida **ao lado do botão**: dentro de um diálogo, uma caixa presa ao
 * `body` ficaria fora da armadilha de foco do Radix, que devolveria o foco ao
 * diálogo antes da cópia — e o navegador copiaria nada, dizendo que copiou.
 */
export const copyText = async (text, near = null) => {
    const { clipboard } = navigator;
    if (clipboard !== undefined) {
        try {
            await clipboard.writeText(text);
            return true;
        }
        catch {
            // Negado (permissão, aba sem foco): tenta o jeito antigo abaixo.
        }
    }
    const host = near?.parentElement ?? document.body;
    const area = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    area.setAttribute('aria-hidden', 'true');
    area.style.position = 'fixed';
    area.style.top = '0';
    area.style.left = '0';
    area.style.opacity = '0';
    host.append(area);
    try {
        area.select();
        // eslint-disable-next-line @typescript-eslint/no-deprecated -- o único jeito de copiar em http://, onde `navigator.clipboard` não existe
        return document.execCommand('copy');
    }
    catch {
        return false;
    }
    finally {
        area.remove();
        near?.focus();
    }
};
/**
 * O botão de copiar, com o retorno À VISTA: "Copiado" por dois segundos, ou o
 * aviso de que não deu — e aí o código continua na tela para copiar à mão.
 */
export const CopyButton = ({ value, label, className, }) => {
    const [state, setState] = useState('idle');
    useEffect(() => {
        if (state === 'idle')
            return;
        const timer = window.setTimeout(() => {
            setState('idle');
        }, 2000);
        return () => {
            window.clearTimeout(timer);
        };
    }, [state]);
    return (_jsxs("span", { className: "inline-flex items-center gap-1", children: [_jsx("button", { type: "button", "aria-label": label, title: label, onClick: (event) => {
                    const button = event.currentTarget;
                    void copyText(value, button).then((ok) => {
                        setState(ok ? 'copied' : 'failed');
                    });
                }, className: cn('inline-flex items-center justify-center rounded-(--radius-sm) p-1 text-(--text-muted)', 'transition-colors hover:bg-(--surface-hover) hover:text-(--text-primary)', 'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--border-focus)', className), children: state === 'copied' ? (_jsx(Check, { "aria-hidden": "true", className: "h-3.5 w-3.5" })) : (_jsx(Copy, { "aria-hidden": "true", className: "h-3.5 w-3.5" })) }), _jsx("span", { role: "status", className: cn('text-(length:--text-xs)', state === 'failed' ? 'text-(--danger)' : 'text-(--success)'), children: state === 'copied'
                    ? 'Copiado'
                    : state === 'failed'
                        ? 'Não copiou — selecione e copie'
                        : '' })] }));
};
/**
 * "Cód. 003216 ⧉" — o rótulo, o valor que dá para selecionar e o copiar.
 *
 * `select-all`: um clique no valor seleciona ele inteiro, para quem prefere o
 * Ctrl+C — e é o caminho que sobra quando a cópia falha.
 */
export const CopyableValue = ({ label, value, what, display, className, }) => (_jsxs("span", { className: cn('inline-flex min-w-0 items-center gap-1 text-(length:--text-xs)', className), children: [_jsx("span", { className: "text-(--text-muted)", children: label }), _jsx("span", { title: display === undefined ? undefined : value, className: "truncate font-(--font-mono) text-(--text-primary) select-all", children: display ?? value }), _jsx(CopyButton, { value: value, label: `Copiar ${what ?? label}` })] }));
//# sourceMappingURL=copy-button.js.map